"use client";

import { Box, Chip, Typography, Grid, Card, Switch, CardContent } from "@mui/joy";
import { CircleCheckBig, CircleAlert, PackagePlus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";

import { MiniInventory } from "../../../components/Inventory/components";
import { Button } from "../../../shared";
import { SideModal } from "../../";
import { ProductCard, ProductCardSimple } from "./ProductCard";

import { useMaterialsInit, useMaterialsUpdate } from "./hooks";
import { SupplierComparison } from "../../SupplierComparison";
import { SelectedSupplierProvider } from "./context";
import { GutterScope } from "./GutterScope";
import { MaterialsSkeleton } from "./skeletons";

export function Materials(props: { measurements: any; setIsNextStageEligible: any; overrideCalculations: boolean }) {
    const { measurements, setIsNextStageEligible, overrideCalculations } = props;

    return (
        <SelectedSupplierProvider>
            <MaterialsContent
                measurements={measurements}
                setIsNextStageEligible={setIsNextStageEligible}
                overrideCalculations={overrideCalculations}
            />
        </SelectedSupplierProvider>
    );
}

function MaterialsContent(props: { measurements: any; setIsNextStageEligible: any; overrideCalculations: boolean }) {
    const { measurements, setIsNextStageEligible, overrideCalculations } = props;

    const {
        missingProducts,
        materialSchema,
        accordionGroupStatus,
        dependencies,
        openAdditionalProductDialog,
        setAccordionGroupStatus,
        setOpenAdditionalProductDialog,
        cart,
        priceRanges,
        isLoading,
    } = useMaterialsInit(measurements);

    const {
        selectedValues,
        computedValues,
        additionalProductsSelected,
        handleExpandGroupedMaterialsClick,
        handleValueChange,
        handleColorChange,
        handleQuantityUpdate,
        handleMiniInventoryContinueClick,
        handleRemoveMaterial,
        handleRemoveAdditionalProduct,
    } = useMaterialsUpdate(
        measurements,
        materialSchema,
        dependencies,
        cart,
        setAccordionGroupStatus,
        setOpenAdditionalProductDialog,
        overrideCalculations,
    );

    if (isLoading) {
        return <MaterialsSkeleton />;
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
        >
            <GutterScope />

            <Typography
                level="title-sm"
                gutterBottom
                sx={{ mb: 1, mt: 2 }}
                startDecorator={<CircleAlert size={16} />}
                color="warning"
            >
                Identified products required for this project that are missing from the supplier's inventory.
            </Typography>

            <Box display={"flex"} gap={1} flexWrap={"wrap"}>
                {missingProducts
                    .filter((e: any) => !e.productFound)
                    .map((e: any) => (
                        <Chip
                            key={e}
                            startDecorator={e.productFound ? <CircleCheckBig size={14} /> : <CircleAlert size={14} />}
                            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 6 }}
                            color={"warning"}
                        >
                            {e}
                        </Chip>
                    ))}
            </Box>

            <Box mt={3}>
                <SideModal
                    portalBoxProps={{
                        minWidth: 1200,
                        width: 1200,
                        maxWidth: "100vw",
                    }}
                    open={openAdditionalProductDialog}
                    onClose={() => setOpenAdditionalProductDialog(false)}
                >
                    <MiniInventory
                        selectedValues={selectedValues}
                        onContinue={handleMiniInventoryContinueClick}
                        materialSchema={materialSchema}
                        priceRanges={priceRanges}
                    />
                </SideModal>

                <Box>
                    <Grid container>
                        {materialSchema.map((product: any, index: number) => {
                            return (
                                <Grid xs={12} key={product.name} mb={1} position={"relative"}>
                                    <AnimatePresence mode="popLayout">
                                        <motion.div
                                            layout
                                            initial={{ opacity: 0, y: -30 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                            transition={{ duration: 0.35, ease: "easeOut", delay: index * 0.001 }}
                                        >
                                            <ProductCard
                                                product={product}
                                                quantityBidding={computedValues[product.Name]}
                                                selectedValues={selectedValues}
                                                handleValueChange={handleValueChange}
                                                handleColorChange={handleColorChange}
                                                handleQuantityUpdate={handleQuantityUpdate}
                                                onRemove={handleRemoveMaterial}
                                                priceRanges={priceRanges}
                                            />
                                        </motion.div>
                                    </AnimatePresence>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>

                {Object.values(additionalProductsSelected).length > 0 && (
                    <Typography level="title-sm" gutterBottom sx={{ mb: 1, mt: 2 }}>
                        Extra Items You’ve Selected
                    </Typography>
                )}

                <Box>
                    <Grid container spacing={1}>
                        {Object.entries(additionalProductsSelected).map(([identifier, product]: [string, any]) => {
                            return (
                                <Grid xs={12} key={identifier}>
                                    <ProductCardSimple
                                        quantityBidding={computedValues[product.Name]}
                                        product={product}
                                        selectedValues={additionalProductsSelected}
                                        handleValueChange={handleValueChange}
                                        handleQuantityUpdate={handleQuantityUpdate}
                                        onRemove={handleRemoveAdditionalProduct}
                                        productIdentifier={identifier}
                                        priceRanges={priceRanges}
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
                <Button
                    variant={"soft"}
                    color={"neutral"}
                    startDecorator={<PackagePlus size={18} />}
                    sx={{ mt: 2, border: "1px solid", borderColor: "divider" }}
                    onClick={() => setOpenAdditionalProductDialog(true)}
                >
                    Add more items
                </Button>
                <SupplierComparison setIsNextStageEligible={setIsNextStageEligible} selectedValues={selectedValues} />
            </Box>
        </motion.div>
    );
}
